import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams  } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import StandardNavbar from '../components/StandardNavbar';
import Footer from '../components/Footer';
import NewsletterSection from '../components/NewsletterSection';
import EuropeSection from '../components/EuropeSection';
import useGTM from '../hooks/useGTM';
import { getDestinoImage } from '../utils/destinoImageMapper.mjs';
import { calcularPrecioFinal } from '../utils/precioCalculator.mjs';
import { Search, Filter, X, MapPin, Calendar, DollarSign, Clock, ChevronDown, ChevronUp, Plus, Minus } from 'lucide-react';



// Después de las importaciones y antes de BuscadorConAutocompletado
const DescuentosBadges = ({ paquete }) => {
  const descuentos = [];

  // Descuento Principal (promocional)
  if (paquete.descuento_principal_monto && parseFloat(paquete.descuento_principal_monto) > 0) {
    const ahora = new Date();
    const desde = paquete.descuento_principal_desde ? new Date(paquete.descuento_principal_desde) : null;
    const hasta = paquete.descuento_principal_hasta ? new Date(paquete.descuento_principal_hasta) : null;
    
    const activo = (!desde || ahora >= desde) && (!hasta || ahora <= hasta);
    
    if (activo) {
      descuentos.push({
        tipo: 'principal',
        texto: paquete.descuento_principal_descripcion || 'Promo especial',
        icono: '🎉',
        color: 'bg-red-100 text-red-700 border-red-300'
      });
    }
  }

  // Descuento 2do Pasajero
  if (paquete.descuento_2do_pasajero_monto && parseFloat(paquete.descuento_2do_pasajero_monto) > 0) {
    const valor = parseFloat(paquete.descuento_2do_pasajero_monto);
    const tipo = paquete.descuento_2do_pasajero_tipo || 'fijo';
    
    descuentos.push({
      tipo: '2do',
      texto: tipo === 'porcentaje' 
        ? `${valor}% OFF 2do pasajero` 
        : `$${valor.toLocaleString()} OFF 2do pasajero`,
      icono: '👥',
      color: 'bg-blue-100 text-blue-700 border-blue-300'
    });
  }

  // Descuento 3er Pasajero
  if (paquete.descuento_3er_pasajero_monto && parseFloat(paquete.descuento_3er_pasajero_monto) > 0) {
    const valor = parseFloat(paquete.descuento_3er_pasajero_monto);
    const tipo = paquete.descuento_3er_pasajero_tipo || 'fijo';
    
    descuentos.push({
      tipo: '3er',
      texto: tipo === 'porcentaje' 
        ? `${valor}% OFF desde 3er pasajero` 
        : `$${valor.toLocaleString()} OFF desde 3er pasajero`,
      icono: '👨‍👩‍👧',
      color: 'bg-green-100 text-green-700 border-green-300'
    });
  }

  // ✅ NUEVO: Family Plan
  if (paquete.family_plan_activo && parseFloat(paquete.family_plan_descuento) > 0) {
    const valor = parseFloat(paquete.family_plan_descuento);
    const tipo = paquete.family_plan_tipo || 'fijo';
    const edadMax = paquete.family_plan_edad_maxima || 12;
    const menoresMax = paquete.family_plan_menores_maximos || 2;
    
    // Usar estilo personalizado si existe
    const estilo = paquete.family_plan_estilo || {};
    const colorFondo = estilo.color_fondo || '#FF6B6B';
    const colorTexto = estilo.color_fuente || '#FFFFFF';
    
    descuentos.push({
      tipo: 'family',
      texto: tipo === 'porcentaje' 
        ? `Family Plan: ${valor}% OFF menores` 
        : `Family Plan: $${valor.toLocaleString()} OFF menores`,
      icono: '👨‍👩‍👧‍👦',
      color: `border-2`,
      estiloCustom: {
        backgroundColor: colorFondo,
        color: colorTexto,
        fontWeight: estilo.texto_negrita ? 'bold' : 'normal'
      },
      tooltip: `Menores de 5 a ${edadMax} años. Hasta ${menoresMax} menores con descuento.`
    });
  }

  if (descuentos.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {descuentos.map((desc, index) => (
        <span
          key={index}
          className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md ${desc.color}`}
          style={desc.estiloCustom}
          title={desc.tooltip || ''}
        >
          <span>{desc.icono}</span>
          <span>{desc.texto}</span>
        </span>
      ))}
    </div>
  );
};


// ✅ LISTÓN DE DESCUENTO - SOLO para descuento principal SIN otros descuentos
const DiscountRibbon = ({ paquete }) => {
  // ════════════════════════════════════════════════════════════════════════════
  // LÓGICA ANTERIOR (COMENTADA)
  // ════════════════════════════════════════════════════════════════════════════
  // const tieneDescuentoPrincipal = paquete.descuento_principal_monto && parseFloat(paquete.descuento_principal_monto) > 0;
  // const tieneDescuento2do = paquete.descuento_2do_pasajero_monto && parseFloat(paquete.descuento_2do_pasajero_monto) > 0;
  // const tieneDescuento3er = paquete.descuento_3er_pasajero_monto && parseFloat(paquete.descuento_3er_pasajero_monto) > 0;
  // const tieneFamilyPlan = paquete.family_plan_activo && parseFloat(paquete.family_plan_descuento) > 0;
  // const tieneDescuento = tieneDescuentoPrincipal || tieneDescuento2do || tieneDescuento3er || tieneFamilyPlan;
  // if (!tieneDescuento) return null;
  // ════════════════════════════════════════════════════════════════════════════
  
  // ✅ NUEVA LÓGICA: Mostrar listón SOLO si:
  // 1. Tiene descuento principal (fijo o porcentaje)
  // 2. NO tiene descuento de 2do pasajero
  // 3. NO tiene descuento de 3er pasajero
  // 4. NO tiene Family Plan activo
  /*
  const tieneDescuentoPrincipal = paquete.descuento_principal_monto && 
                                   parseFloat(paquete.descuento_principal_monto) > 0;
  
  const tieneDescuento2do = paquete.descuento_2do_pasajero_monto && 
                             parseFloat(paquete.descuento_2do_pasajero_monto) > 0;
  
  const tieneDescuento3er = paquete.descuento_3er_pasajero_monto && 
                             parseFloat(paquete.descuento_3er_pasajero_monto) > 0;
  
  const tieneFamilyPlan = paquete.family_plan_activo && 
                          parseFloat(paquete.family_plan_descuento) > 0;
  */
  // ✅ SOLO mostrar si tiene descuento principal Y NO tiene otros descuentos
  ///const mostrarListon = tieneDescuentoPrincipal && 
  //                      !tieneDescuento2do && 
  //                      !tieneDescuento3er && 
  //                      !tieneFamilyPlan;
  
  //if (!mostrarListon) return null;

  // ✅ NUEVA LÓGICA CORREGIDA: Mostrar listón SIEMPRE que tenga descuento principal
  // (sin importar si tiene otros descuentos)

const tieneDescuentoPrincipal = paquete.descuento_principal_monto && 
                                 parseFloat(paquete.descuento_principal_monto) > 0;

// ✅ Mostrar listón si tiene descuento principal, punto.
if (!tieneDescuentoPrincipal) return null;

  return (
    <div className="absolute top-0 right-0 z-10 overflow-hidden" style={{ width: '120px', height: '120px' }}>
      <div 
        className="absolute transform rotate-45 bg-red-600 text-white text-center font-bold py-1 shadow-lg"
        style={{
          width: '170px',
          top: '25px',
          right: '-43px',
          fontSize: '13px',
          letterSpacing: '0.5px'
        }}
      >
        OFERTA
      </div>
    </div>
  );
};



// ✅ COMPONENTE FUERA DE LA FUNCIÓN PRINCIPAL
const BuscadorConAutocompletado = ({ destinos, value, onChange, packages }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredDestinos, setFilteredDestinos] = useState([]);
  const [inputValue, setInputValue] = useState(value);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null); // ✅ NUEVO: ref para el input

  // Sincronizar inputValue cuando value cambia desde afuera
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ NUEVO: Filtrar con ordenamiento inteligente
  useEffect(() => {
    if (inputValue && inputValue.length > 0) {
      const searchTerm = inputValue.toLowerCase();
      
      const filtered = destinos
        .filter(destino => destino && destino.toLowerCase().includes(searchTerm))
        .sort((a, b) => {
          const aLower = a.toLowerCase();
          const bLower = b.toLowerCase();
          
          // Coincidencia exacta primero
          if (aLower === searchTerm) return -1;
          if (bLower === searchTerm) return 1;
          
          // Comienza con el término de búsqueda
          if (aLower.startsWith(searchTerm) && !bLower.startsWith(searchTerm)) return -1;
          if (bLower.startsWith(searchTerm) && !aLower.startsWith(searchTerm)) return 1;
          
          // Destinos sin guión antes que combinados
          const aEsCombinado = a.includes('-');
          const bEsCombinado = b.includes('-');
          if (!aEsCombinado && bEsCombinado) return -1;
          if (aEsCombinado && !bEsCombinado) return 1;
          
          // Alfabético como último criterio
          return a.localeCompare(b);
        });
      
      setFilteredDestinos(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredDestinos([]);
      setShowSuggestions(false);
    }
  }, [inputValue, destinos]);

  // ✅ CORREGIDO: Cierre inmediato
  const handleSelectDestino = (destino) => {
    setInputValue(destino);
    onChange(destino);
    
    // Cerrar inmediatamente
    setShowSuggestions(false);
    setFilteredDestinos([]);
    
    // Forzar blur del input
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  return (
    <div className="flex-1 relative w-full" ref={wrapperRef}>
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={20} />
      <input
        ref={inputRef} // ✅ NUEVO: agregar ref
        type="text"
        placeholder="Buscar por destino... (ej: Bariloche, Mar del Plata)"
        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2AA8B0]"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (inputValue && filteredDestinos.length > 0) {
            setShowSuggestions(true);
          }
        }}
      />
      
      {showSuggestions && filteredDestinos.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredDestinos.map((destino, index) => (
            <button
              key={index}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault(); // Prevenir que el input pierda el foco antes de seleccionar
                handleSelectDestino(destino);
              }}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center gap-2 transition-colors"
            >
              <MapPin size={16} className="text-[#2AA8B0]" />
              <span className="text-gray-700">{destino}</span>
              <span className="ml-auto text-xs text-gray-400">
                {packages.filter(p => 
                  (p.destino_nombre || p.destino || '').toLowerCase().includes(destino.toLowerCase())
                ).length} paquetes
              </span>
            </button>
          ))}
        </div>
      )}
      
      {inputValue && inputValue.length > 0 && filteredDestinos.length === 0 && showSuggestions && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm">
          No se encontraron destinos con "{inputValue}"
        </div>
      )}
    </div>
  );
};

function Paquetes() {
  const location = useLocation();
  const navigate = useNavigate();
  const { destino: destinoParam } = useParams();
  const { trackSearch, trackFilterApply, trackPaqueteView } = useGTM();
  
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    destino: '',
    categoria: '',
    precioMin: 0,
    precioMax: 1000000,
    fechaInicio: '',
    fechaFin: '',
    duracion: '',
    transporte: '',
    pasajeros: 0,
    puntoAscenso: '',
    destaque: ''
  });

  // Estados para opciones de filtros
  const [filterOptions, setFilterOptions] = useState({
    destinos: [],
    categorias: [],
    duraciones: [],
    transportes: [],
    puntosAscenso: []
  });

  const [destaques, setDestaques] = useState([]);

  const [paginaActual, setPaginaActual] = useState(1);
  const PAQUETES_POR_PAGINA = 10;

  // Scroll al inicio cuando cambia la página
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [paginaActual]);


// Obtener parámetros de búsqueda desde URL
useEffect(() => {
  // ✅ NUEVO: Si viene un destino/promoción en la URL como parámetro
  if (destinoParam && destaques.length > 0) {
    const paramLower = destinoParam.toLowerCase();
    
    // Verificar si es una promoción
    const promocionEncontrada = destaques.find(d => {
      const slug = d.nombre
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      return slug === paramLower;
    });
    
    if (promocionEncontrada) {
      console.log('🎯 Promoción detectada en URL:', promocionEncontrada.nombre);
      setFilters(prev => ({
        ...prev,
        destaque: promocionEncontrada.id.toString(),
        destino: '' // Limpiar destino si es promoción
      }));
      return;
    }
    
    // Si no es promoción, es un destino
    console.log('🌍 Destino detectado en URL:', destinoParam);
    setFilters(prev => ({
      ...prev,
      destino: decodeURIComponent(destinoParam),
      destaque: '' // Limpiar promoción si es destino
    }));
    return;
  }

  const params = new URLSearchParams(location.search);
  
  console.log('📋 Leyendo parámetros de URL:', location.search);
  
  // ✅ Construir nuevos filtros COMPLETOS desde los parámetros de URL
  const newFilters = {
    destino: params.get('destino') || '',
    categoria: params.get('categoria') || '',
    precioMin: params.get('precioMin') ? parseInt(params.get('precioMin')) : 0,
    precioMax: params.get('precioMax') ? parseInt(params.get('precioMax')) : 1000000,
    fechaInicio: params.get('fechaDesde') || params.get('fecha') || '',
    fechaFin: params.get('fechaHasta') || '',
    duracion: params.get('duracion') || '',
    transporte: params.get('transporte') || '',
    pasajeros: params.get('pasajeros') ? parseInt(params.get('pasajeros')) : 0,
    puntoAscenso: params.get('puntoAscenso') || '',
    destaque: params.get('destaque') || ''
  };
  
  console.log('📋 Filtros aplicados desde URL:', newFilters);
  
  setFilters(newFilters);
  
  // Track búsqueda si viene de parámetros
  if (params.get('destino')) {
    trackSearch({
      destino: params.get('destino'),
      transporte: params.get('transporte') || '',
      pasajeros: parseInt(params.get('pasajeros')) || 1,
      fecha: params.get('fecha') || ''
    });
  }
}, [location.search, destinoParam, destaques]); // ← AGREGAR destaques como dependencia


  // Cargar paquetes
// Cargar paquetes
useEffect(() => {
  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/paquetes`);
      if (response.ok) {
        const data = await response.json();
        
        setPackages(data);
        setFilteredPackages(data);

        // Extraer opciones para filtros
        const destinos = [...new Set(data.map(p => p.destino_nombre || p.destino).filter(Boolean))];
        const categorias = [...new Set(data.map(p => p.categoria))].filter(Boolean);
        const duraciones = [...new Set(data.map(p => p.duracion))].filter(Boolean);
        const transportes = [...new Set(
          data.map(p => p.transporte).filter(Boolean)
        )].sort();

        setFilterOptions(prev => ({
          ...prev,
          destinos,
          categorias,
          duraciones,
          transportes 
        }));
        
        console.log('✅ Paquetes cargados:', data.length);
      }
    } catch (error) {
      console.error('❌ Error cargando paquetes:', error);
    } finally {
      setLoading(false);
    }
  };

  fetchPackages();
}, []);

  // Cargar puntos de ascenso
useEffect(() => {
  const fetchPuntosAscenso = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/puntos-ascenso-local`);
      if (response.ok) {
        const data = await response.json();
        if (data.puntos_de_ascenso) {
          setFilterOptions(prev => ({ 
            ...prev, 
            puntosAscenso: data.puntos_de_ascenso 
          }));
          console.log('📍 Puntos cargados:', data.puntos_de_ascenso.length);
        }
      }
    } catch (error) {
      console.error('❌ Error cargando puntos:', error);
    }
  };

  fetchPuntosAscenso();
}, []);



// ✅ Cargar destaques CON validación de paquetes
useEffect(() => {
  const fetchDestaques = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/destaques`);
      if (response.ok) {
        const data = await response.json();
        
        // ✅ NUEVO: Filtrar solo destaques que tienen paquetes asociados
        const destaquesConPaquetes = [];
        
        for (const destaque of data) {
          try {
            const pkgResponse = await fetch(`${process.env.REACT_APP_API_URL}/destaques/${destaque.id}/paquetes`);
            if (pkgResponse.ok) {
              const paquetes = await pkgResponse.json();
              if (paquetes && paquetes.length > 0) {
                destaquesConPaquetes.push(destaque);
                console.log(`✅ Destaque "${destaque.nombre}": ${paquetes.length} paquetes`);
              } else {
                console.log(`⚠️ Destaque "${destaque.nombre}": sin paquetes (omitido)`);
              }
            }
          } catch (error) {
            console.warn(`Error verificando paquetes del destaque ${destaque.id}:`, error);
          }
        }
        
        setDestaques(destaquesConPaquetes);
        console.log(`📊 Total destaques con paquetes: ${destaquesConPaquetes.length}`);
      }
    } catch (error) {
      console.error('Error cargando destaques:', error);
    }
  };
  
  fetchDestaques();
}, []);


// Aplicar filtros
useEffect(() => {
  const aplicarFiltrosAsync = async () => {
    console.log('\n🔧 ============ APLICANDO FILTROS ============');
    console.log('📋 URL actual:', location.search);
    console.log('📋 Filters state:', filters);
    
    let filtered = [...packages];
    console.log('📦 Total paquetes inicial:', filtered.length);

    // ✅ Excluir paquetes sin disponibilidad (0 cupos)
    filtered = filtered.filter(pkg => pkg.disponibilidad && parseInt(pkg.disponibilidad) > 0);
    console.log('📦 Después de filtrar disponibilidad > 0:', filtered.length);

    // ✅ CRÍTICO: Si viene paqueteId, mostrar SOLO ese paquete
    const params = new URLSearchParams(location.search);
    const paqueteId = params.get('paqueteId');
    const fechaParam = params.get('fecha');
    const pasajerosParam = params.get('pasajeros');
    
    console.log('🔍 Parámetros URL:');
    console.log('   paqueteId:', paqueteId);
    console.log('   fecha:', fechaParam);
    console.log('   pasajeros:', pasajerosParam);
    
    if (paqueteId) {
      const id = parseInt(paqueteId);
      filtered = packages.filter(pkg => pkg.id === id);
      
      console.log('🎯 Filtrando por paqueteId:', id);
      console.log('   Paquetes encontrados:', filtered.length);
      
      setFilteredPackages(filtered);
      setPaginaActual(1);
      console.log('============ FIN FILTROS ============\n');
      return;
    }

    // ✅ FILTRO POR DESTAQUE (ANTES de otros filtros)
    if (filters.destaque) {
      const destaqueId = parseInt(filters.destaque);
      
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/destaques/${destaqueId}/paquetes`);
        if (response.ok) {
          const paquetesDestaque = await response.json();
          const paquetesIds = paquetesDestaque.map(p => p.id);
          filtered = filtered.filter(pkg => paquetesIds.includes(pkg.id));
          console.log(`🏷️ Filtrado por destaque ${destaqueId}: ${filtered.length} paquetes`);
        }
      } catch (error) {
        console.error('❌ Error filtrando por destaque:', error);
      }
    }

    // ✅ FILTRO POR DESTINO
    if (filters.destino) {
      const searchTerm = filters.destino.toLowerCase();
      
      filtered = filtered.filter(pkg => 
        (pkg.destino_nombre || pkg.destino || '').toLowerCase().includes(searchTerm)
      ).sort((a, b) => {
        const aNombre = (a.destino_nombre || a.destino || '').toLowerCase();
        const bNombre = (b.destino_nombre || b.destino || '').toLowerCase();
        
        if (aNombre === searchTerm) return -1;
        if (bNombre === searchTerm) return 1;
        
        if (aNombre.startsWith(searchTerm) && !bNombre.startsWith(searchTerm)) return -1;
        if (bNombre.startsWith(searchTerm) && !aNombre.startsWith(searchTerm)) return 1;
        
        const aEsCombinado = aNombre.includes('-');
        const bEsCombinado = bNombre.includes('-');
        if (!aEsCombinado && bEsCombinado) return -1;
        if (aEsCombinado && !bEsCombinado) return 1;
        
        const fechaA = a.fecha_salida ? new Date(a.fecha_salida) : new Date('2099-12-31');
        const fechaB = b.fecha_salida ? new Date(b.fecha_salida) : new Date('2099-12-31');
        
        return fechaB - fechaA;
      });
      
      console.log(`🌍 Filtrado por destino "${filters.destino}": ${filtered.length} paquetes`);
    }

    // ✅ FILTRO POR CATEGORÍA
    if (filters.categoria) {
      filtered = filtered.filter(pkg => pkg.categoria === filters.categoria);
      console.log(`📂 Filtrado por categoría "${filters.categoria}": ${filtered.length} paquetes`);
    }

    // ✅ FILTRO POR PRECIO
    if (filters.precioMin > 0 || filters.precioMax < 1000000) {
      filtered = filtered.filter(pkg => 
        pkg.tarifa >= filters.precioMin && pkg.tarifa <= filters.precioMax
      );
      console.log(`💰 Filtrado por precio: ${filtered.length} paquetes`);
    }

    // ✅ FILTRO POR FECHA EXACTA (si viene desde SearchComponent via URL Y NO hay rango definido)
    const fechaDesdeParam = params.get('fechaDesde');
    const fechaHastaParam = params.get('fechaHasta');

    if (fechaParam && !fechaDesdeParam && !fechaHastaParam) {
      console.log(`📅 Aplicando filtro de FECHA EXACTA desde URL: ${fechaParam}`);
      
      filtered = filtered.filter(pkg => {
        if (!pkg.fecha_salida) return false;
        
        const fechaPaquete = new Date(pkg.fecha_salida).toISOString().split('T')[0];
        const match = fechaPaquete === fechaParam;
        
        if (match) {
          console.log(`   ✅ Match: [${pkg.id}] ${pkg.titulo} - ${fechaPaquete}`);
        }
        
        return match;
      });
      
      console.log(`   📊 Total con fecha ${fechaParam}: ${filtered.length} paquetes`);
    }
    // ✅ FILTRO POR RANGO DE FECHAS (si viene de los filtros manuales del sidebar)
else {
  if (filters.fechaInicio) {
    filtered = filtered.filter(pkg => {
      if (!pkg.fecha_salida) return false;
      // ✅ CORREGIDO: Comparar como strings YYYY-MM-DD para evitar problemas de timezone
      const fechaPkgStr = pkg.fecha_salida.split('T')[0]; // "2025-12-02"
      const fechaFiltroStr = filters.fechaInicio; // "2025-12-02"
      return fechaPkgStr >= fechaFiltroStr;
    });
    console.log(`📅 Filtrado por fecha inicio (>= ${filters.fechaInicio}): ${filtered.length} paquetes`);
  }

  if (filters.fechaFin) {
    filtered = filtered.filter(pkg => {
      if (!pkg.fecha_salida) return false;
      // ✅ CORREGIDO: Comparar como strings YYYY-MM-DD para evitar problemas de timezone
      const fechaPkgStr = pkg.fecha_salida.split('T')[0]; // "2025-12-02"
      const fechaFiltroStr = filters.fechaFin; // "2025-12-18"
      return fechaPkgStr <= fechaFiltroStr;
    });
    console.log(`📅 Filtrado por fecha fin (<= ${filters.fechaFin}): ${filtered.length} paquetes`);
  }
}

    // ✅ FILTRO POR DURACIÓN
    if (filters.duracion) {
      filtered = filtered.filter(pkg => 
        pkg.duracion && pkg.duracion.includes(filters.duracion)
      );
      console.log(`⏱️ Filtrado por duración: ${filtered.length} paquetes`);
    }

    // ✅ FILTRO POR PUNTO DE ASCENSO
    if (filters.puntoAscenso) {
      filtered = filtered.filter(pkg =>
        pkg.puntos_ascenso_ids && 
        Array.isArray(pkg.puntos_ascenso_ids) &&
        pkg.puntos_ascenso_ids.includes(parseInt(filters.puntoAscenso))
      );
      console.log(`📍 Filtrado por punto ascenso: ${filtered.length} paquetes`);
    }

    // ✅ FILTRO POR TRANSPORTE
    if (filters.transporte) {
      filtered = filtered.filter(pkg => 
        pkg.transporte && pkg.transporte.toLowerCase().includes(filters.transporte.toLowerCase())
      );
      console.log(`🚌 Filtrado por transporte: ${filtered.length} paquetes`);
    }

    // ✅ FILTRO POR PASAJEROS (desde URL o desde filtros)
    const numPasajeros = pasajerosParam ? parseInt(pasajerosParam) : filters.pasajeros;
    
    if (numPasajeros === 1) {
      console.log('👤 Aplicando filtro: 1 pasajero (SOLO SINGLES)');
      filtered = filtered.filter(pkg => {
        const singlesValue = (pkg.singles || '').toLowerCase().trim();
        const esSingle = singlesValue === 'si' || singlesValue === 'sí';
        
        console.log(`   [${pkg.id}] ${pkg.titulo}: singles="${pkg.singles}" → ${esSingle ? '✅' : '❌'}`);
        
        return esSingle;
      });
      console.log(`   📊 Total singles: ${filtered.length} paquetes`);
    } else if (numPasajeros >= 2) {
      console.log(`👥 Aplicando filtro: ${numPasajeros} pasajeros (SOLO NO-SINGLES)`);
      filtered = filtered.filter(pkg => {
        const singlesValue = (pkg.singles || '').toLowerCase().trim();
        const esSingle = singlesValue === 'si' || singlesValue === 'sí';
        const cumpleDisponibilidad = pkg.disponibilidad >= numPasajeros;
        
        const cumple = !esSingle && cumpleDisponibilidad;
        
        console.log(`   [${pkg.id}] ${pkg.titulo}: singles="${pkg.singles}", disponibilidad=${pkg.disponibilidad} → ${cumple ? '✅' : '❌'}`);
        
        return cumple;
      });
      console.log(`   📊 Total dobles/triples: ${filtered.length} paquetes`);
    }

    // ✅ ORDENAR según si hay filtro de destino activo o no
    if (filters.destino) {
      // CON FILTRO DE DESTINO: Singles al final, luego por fecha
      filtered = filtered.sort((a, b) => {
        // 1. Singles siempre al final
        const aEsSingle = (a.singles || '').toLowerCase().trim() === 'si' || (a.singles || '').toLowerCase().trim() === 'sí';
        const bEsSingle = (b.singles || '').toLowerCase().trim() === 'si' || (b.singles || '').toLowerCase().trim() === 'sí';
        
        if (aEsSingle && !bEsSingle) return 1;  // a va después
        if (!aEsSingle && bEsSingle) return -1; // b va después
        
        // 2. Mismo tipo: ordenar por fecha (más próxima primero)
        const fechaA = a.fecha_salida ? new Date(a.fecha_salida) : new Date('2099-12-31');
        const fechaB = b.fecha_salida ? new Date(b.fecha_salida) : new Date('2099-12-31');
        return fechaA - fechaB;
      });
    } else {
      // SIN FILTRO DE DESTINO: Solo por fecha (más próxima primero)
      filtered = filtered.sort((a, b) => {
        const fechaA = a.fecha_salida ? new Date(a.fecha_salida) : new Date('2099-12-31');
        const fechaB = b.fecha_salida ? new Date(b.fecha_salida) : new Date('2099-12-31');
        return fechaA - fechaB;
      });
    }

    console.log('✅ TOTAL FINAL FILTRADO:', filtered.length, 'paquetes');
    console.log('============ FIN FILTROS ============\n');
    
    setFilteredPackages(filtered);
    setPaginaActual(1);
  };
  
  aplicarFiltrosAsync();
}, [packages, filters, location.search]);



// Limpiar filtros cuando se navega directamente a /paquetes (sin parámetros)
useEffect(() => {
  // Si la URL es exactamente /paquetes (sin destino ni parámetros)
  if (location.pathname === '/paquetes' && !location.search && !destinoParam) {
    setFilters({
      destino: '',
      categoria: '',
      precioMin: 0,
      precioMax: 1000000,
      fechaInicio: '',
      fechaFin: '',
      duracion: '',
      transporte: '',
      pasajeros: 0,
      puntoAscenso: ''
    });
  }
}, [location.pathname, location.search, destinoParam]);

  
  const handleFilterChange = (filterName, value) => {
  console.log('🔄 handleFilterChange llamado:', filterName, '=', value);

  // Crear nuevo estado de filtros PRESERVANDO los existentes
  const newFilters = { ...filters, [filterName]: value };

  // ✅ REMOVIDO: Ya no limpiamos destino al seleccionar destaque
  // Esto permite filtrar por destino + categoría/promoción simultáneamente
  // Solo limpiamos destaque si se escribe un destino nuevo
  if (filterName === 'destino' && value) {
    // Si el usuario busca un destino específico, quitar filtro de promoción
    // newFilters.destaque = ''; // ← Comentado para permitir combinación
  }

  
  // Actualizar estado
  setFilters(newFilters);
  
  // Construir URL con todos los filtros activos
  const params = new URLSearchParams();

  if (newFilters.destino) params.set('destino', newFilters.destino);
  if (newFilters.transporte) params.set('transporte', newFilters.transporte);
  if (newFilters.pasajeros > 0) params.set('pasajeros', newFilters.pasajeros.toString());
  if (newFilters.puntoAscenso) params.set('puntoAscenso', newFilters.puntoAscenso);

  // ✅ NUEVO: Si hay rango de fechas, NO incluir fecha exacta
  if (newFilters.fechaInicio || newFilters.fechaFin) {
    // Usar parámetros de rango
    if (newFilters.fechaInicio) params.set('fechaDesde', newFilters.fechaInicio);
    if (newFilters.fechaFin) params.set('fechaHasta', newFilters.fechaFin);
    // NO poner 'fecha' (exacta)
  } else {
    // Mantener fecha exacta si venía de URL y no se modificó el rango
    const currentParams = new URLSearchParams(location.search);
    const fechaExacta = currentParams.get('fecha');
    if (fechaExacta) params.set('fecha', fechaExacta);
  }

  if (newFilters.duracion) params.set('duracion', newFilters.duracion);
  if (newFilters.destaque) params.set('destaque', newFilters.destaque);
  
  // Construir URL final
  const queryString = params.toString();
  const newUrl = queryString ? `/paquetes?${queryString}` : '/paquetes';
  
  console.log('🌐 Nueva URL:', newUrl);
  navigate(newUrl, { replace: true });
  
  // Track aplicación de filtro
  trackFilterApply(newFilters);
};

  const clearFilters = () => {
    setFilters({
      destino: '',
      categoria: '',
      precioMin: 0,
      precioMax: 1000000,
      fechaInicio: '',
      fechaFin: '',
      duracion: '',
      transporte: '',
      pasajeros: 0,
      puntoAscenso: '',
      destaque: ''
    });
    navigate('/paquetes', { replace: true });
  };

  const handlePaqueteClick = (paquete) => {
    trackPaqueteView(paquete);
  };

  // PARTE 5: Componente para mostrar icono de transporte
  const TransporteIcon = ({ transporte }) => {
    const esAereo = transporte === 'Aéreo' || transporte === 'Aereo' || transporte === 'Avión';
    
    return (
      <div className="flex items-center gap-2">
        {esAereo ? (
          <span className="text-2xl" title="Aéreo">✈️</span>
        ) : transporte === 'Bus' || transporte === 'Ómnibus' ? (
          <span className="text-2xl" title="Bus">🚌</span>
        ) : transporte === 'Crucero' ? (
          <span className="text-2xl" title="Crucero">🚢</span>
        ) : transporte === 'Auto' || transporte === 'Automóvil' ? (
          <span className="text-2xl" title="Auto">🚗</span>
        ) : (
          <span className="text-2xl" title={transporte || 'Bus'}>🚌</span>
        )}
        <span className="text-sm text-gray-600">
          {transporte || 'Bus'}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2AA8B0] mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando paquetes...</p>
        </div>
      </div>
    );
  }

  // Calcular paginación
  const totalPaginas = Math.ceil(filteredPackages.length / PAQUETES_POR_PAGINA);
  const indiceInicio = (paginaActual - 1) * PAQUETES_POR_PAGINA;
  const indiceFin = indiceInicio + PAQUETES_POR_PAGINA;
  const paquetesPaginados = filteredPackages.slice(indiceInicio, indiceFin);

  return (
    <>
      <Helmet>
        <title>Paquetes | Noche y Día Turismo</title>
        <meta name="description" content="Explora todos los paquetes de viaje disponibles." />
      </Helmet>
      
      <StandardNavbar activeLink="paquetes" />
      
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2AA8B0] to-[#1E3A5F] text-white py-8">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl font-bold mb-2">Paquetes de Viaje</h1>
            <p className="text-blue-100">Descubre experiencias únicas en destinos increíbles</p>
          </div>
        </div>

        {/* Barra de búsqueda y filtros mobile */}
        <div className="bg-white shadow-sm border-b">
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* ✅ Búsqueda con autocompletado */}
              <BuscadorConAutocompletado
                destinos={filterOptions.destinos}
                value={filters.destino}
                onChange={(value) => handleFilterChange('destino', value)}
                packages={packages}
              />
              
              {/* Botón filtros mobile */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden flex items-center gap-2 px-4 py-2 bg-[#2AA8B0] text-white rounded-lg"
              >
                <Filter size={16} />
                Filtros
                {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              
              {/* Resultados */}
              <div className="text-gray-600 text-sm whitespace-nowrap">
                {filteredPackages.length} de {packages.length} paquetes
              </div>
            </div>
          </div>
        </div>

        {/* Resto del código igual... */}
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Panel de filtros */}
            <div className={`lg:w-1/4 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-gray-900">Filtros</h2>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-[#2AA8B0] hover:text-[#1E3A5F] flex items-center gap-1"
                  >
                    <X size={16} />
                    Limpiar
                  </button>
                </div>

                <div className="space-y-6">

                  {/* ✅ NUEVO: Filtro por Destaque/Promoción */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin size={16} className="inline mr-1" />
                        Categoría
                      </label>
                      <select
                      className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:border-[#2AA8B0]"
                      value={filters.destaque}
                      onChange={(e) => handleFilterChange('destaque', e.target.value)}
                    >
                      <option value="">Todas las promociones</option>
                      {destaques.map(destaque => (
                        <option key={destaque.id} value={destaque.id}>
                          {destaque.icono} {destaque.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ✅ COMENTADO: Filtro por categoría 
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin size={16} className="inline mr-1" />
                      Categoría
                    </label>
                    <select 
                      className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:border-[#2AA8B0]"
                      value={filters.categoria}
                      onChange={(e) => handleFilterChange('categoria', e.target.value)}
                    >
                      <option value="">Todas las categorías</option>
                      {filterOptions.categorias.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  */}

                  {/* ✅ NUEVO: Filtro por Transporte */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🚌 Tipo de Transporte
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:border-[#2AA8B0]"
                      value={filters.transporte}
                      onChange={(e) => handleFilterChange('transporte', e.target.value)}
                    >
                      <option value="">Todos los transportes</option>
                      {filterOptions.transportes.map(transp => (
                        <option key={transp} value={transp}>
                          {transp === 'Aéreo' || transp === 'Aereo' ? '✈️' : '🚌'} {transp}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ✅ NUEVO: Filtro por Cantidad de Pasajeros */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      👥 Cantidad de Pasajeros
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleFilterChange('pasajeros', Math.max(1, filters.pasajeros - 1))}
                        className="w-10 h-10 rounded-full border-2 border-[#2AA8B0] text-[#2AA8B0] hover:bg-[#2AA8B0] hover:text-white transition-colors flex items-center justify-center"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="text-xl font-semibold text-gray-900 min-w-[3rem] text-center">
                        {filters.pasajeros}
                      </span>
                      <button
                        onClick={() => handleFilterChange('pasajeros', Math.min(10, filters.pasajeros + 1))}
                        className="w-10 h-10 rounded-full border-2 border-[#2AA8B0] text-[#2AA8B0] hover:bg-[#2AA8B0] hover:text-white transition-colors flex items-center justify-center"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Se mostrarán paquetes con disponibilidad para {filters.pasajeros} {filters.pasajeros === 1 ? 'persona' : 'personas'}
                    </p>
                  </div>

                  {/* Filtro por Punto de Ascenso */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin size={16} className="inline mr-1" />
                      Punto de Ascenso
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:border-[#2AA8B0]"
                      value={filters.puntoAscenso}
                      onChange={(e) => handleFilterChange('puntoAscenso', e.target.value)}
                    >
                      <option value="">Todos los puntos</option>
                      {filterOptions.puntosAscenso?.length > 0 && filterOptions.puntosAscenso.map(punto => (
                        <option key={punto.id} value={punto.id}>{punto.nombre}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por fechas */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar size={16} className="inline mr-1" />
                      Fechas de viaje
                    </label>
                    <div className="space-y-2">
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:border-[#2AA8B0]"
                        value={filters.fechaInicio}
                        onChange={(e) => handleFilterChange('fechaInicio', e.target.value)}
                      />
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:border-[#2AA8B0]"
                        value={filters.fechaFin}
                        onChange={(e) => handleFilterChange('fechaFin', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Filtro por duración */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock size={16} className="inline mr-1" />
                      Duración
                    </label>
                    <select 
                      className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:border-[#2AA8B0]"
                      value={filters.duracion}
                      onChange={(e) => handleFilterChange('duracion', e.target.value)}
                    >
                      <option value="">Cualquier duración</option>
                      <option value="1">1 día</option>
                      <option value="2">2 días</option>
                      <option value="3">3 días</option>
                      <option value="4">4 días</option>
                      <option value="5">5 días o más</option>
                    </select>
                  </div>

                  {/* Filtros activos */}
                  {(filters.destino || filters.categoria || filters.puntoAscenso || filters.fechaInicio || filters.transporte || filters.pasajeros > 0 || filters.destaque) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Filtros activos</label>
                      <div className="flex flex-wrap gap-2">
                        {filters.destino && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#2AA8B0] text-white text-xs rounded-full">
                            {filters.destino}
                            <button onClick={() => handleFilterChange('destino', '')}>
                              <X size={12} />
                            </button>
                          </span>
                        )}
                        {filters.categoria && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
                            {filters.categoria}
                            <button onClick={() => handleFilterChange('categoria', '')}>
                              <X size={12} />
                            </button>
                          </span>
                        )}
                        {filters.puntoAscenso && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500 text-white text-xs rounded-full">
                            Punto: {filterOptions.puntosAscenso.find(p => p.id === parseInt(filters.puntoAscenso))?.nombre?.substring(0, 20) || filters.puntoAscenso}...
                            <button onClick={() => handleFilterChange('puntoAscenso', '')}>
                              <X size={12} />
                            </button>
                          </span>
                        )}

                        {filters.destaque && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-500 text-white text-xs rounded-full">
                            {destaques.find(d => d.id === parseInt(filters.destaque))?.icono} {destaques.find(d => d.id === parseInt(filters.destaque))?.nombre}
                            <button onClick={() => handleFilterChange('destaque', '')}>
                              <X size={12} />
                            </button>
                          </span>
                        )}
                        {filters.transporte && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-500 text-white text-xs rounded-full">
                            🚌 {filters.transporte}
                            <button onClick={() => handleFilterChange('transporte', '')}>
                              <X size={12} />
                            </button>
                          </span>
                        )}
                        {filters.pasajeros > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-500 text-white text-xs rounded-full">
                            👥 {filters.pasajeros} {filters.pasajeros === 1 ? 'pasajero' : 'pasajeros'}
                            <button onClick={() => handleFilterChange('pasajeros', 0)}>
                              <X size={12} />
                            </button>
                          </span>
                        )}
                        {filters.fechaInicio && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-500 text-white text-xs rounded-full">
                            📅 {new Date(filters.fechaInicio + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            <button onClick={() => handleFilterChange('fechaInicio', '')}>
                              <X size={12} />
                            </button>
                          </span>
                        )}
                        {filters.fechaFin && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-600 text-white text-xs rounded-full">
                            📅 hasta {new Date(filters.fechaFin + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            <button onClick={() => handleFilterChange('fechaFin', '')}>
                              <X size={12} />
                            </button>
                          </span>
                        )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Lista de paquetes */}
            <div className="flex-1">
              {filteredPackages.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                  <div className="text-gray-400 mb-4">
                    <Search size={48} className="mx-auto" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    No se encontraron paquetes
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Intenta ajustar tus filtros para encontrar más opciones
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-6 py-2 bg-[#2AA8B0] text-white rounded-lg hover:bg-[#1E3A5F] transition-colors"
                  >
                    Limpiar filtros
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {paquetesPaginados.map((pkg) => (
                    <div 
                      key={pkg.id} 
                      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer relative"
                      onClick={(e) => {
                        handlePaqueteClick(pkg);
                        // Si es Ctrl+Click o Click medio, abrir en nueva pestaña
                        if (e.ctrlKey || e.metaKey || e.button === 1) {
                          window.open(`/paquete/${pkg.id}`, '_blank');
                        } else {
                          navigate(`/paquete/${pkg.id}`);
                        }
                      }}
                    >
                      {/* ✅ LISTÓN DE DESCUENTO EN LA CARD COMPLETA */}
                      <DiscountRibbon paquete={pkg} />

                      <div className="overflow-hidden rounded-lg">
                        <div className="flex flex-col md:flex-row ">
                          {/* Imagen */}
                          <div className="md:w-1/3">
                            <img 
                              src={getDestinoImage(pkg.destino_nombre || pkg.destino || pkg.titulo)} 
                              alt={pkg.titulo}
                              className="w-full h-48 md:h-64 object-cover object-center"
                              onError={(e) => {
                                e.target.src = 'https://placehold.co/400x250/2AA8B0/white?text=Sin+Imagen';
                              }}
                            />

                            {/* ✅ NUEVO: Badges de Destaques */}
                            {pkg.destaques && pkg.destaques.length > 0 && (
                              <div className="absolute top-2 left-2 flex flex-col gap-1">
                                {pkg.destaques.map(destaque => (
                                  <span 
                                    key={destaque.id}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg"
                                    style={{ backgroundColor: destaque.color }}
                                    title={destaque.descripcion}
                                  >
                                    <span className="text-base">{destaque.icono}</span>
                                    <span>{destaque.nombre}</span>
                                  </span>
                                ))}
                              </div>
                            )}

                          </div>
                          
                          {/* Contenido */}
                          <div className="md:w-2/3 p-6 flex flex-col justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  pkg.categoria === 'nacional' ? 'bg-green-100 text-green-800' :
                                  pkg.categoria === 'internacional' ? 'bg-blue-100 text-blue-800' :
                                  'bg-purple-100 text-purple-800'
                                }`}>
                                  {pkg.categoria}
                                </span>
                                {pkg.destacado && (
                                  <span className="px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">
                                    ⭐ Destacado
                                  </span>
                                )}
                              </div>
                              
                              <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {pkg.titulo}
                              </h3>
                              {/* DESCRIPCION CARDS
                              <p className="text-gray-600 mb-3 line-clamp-2">
                                {pkg.descripcion}
                              </p>
                              */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500 mb-4">
                                {/* ✅ NUEVO: Transporte con icono */}
                                {pkg.transporte && (
                                  <div className="flex items-center gap-1">
                                    <TransporteIcon transporte={pkg.transporte} />
                                  </div>
                                )}
                                {pkg.hotel && (
                                  <div className="flex items-center gap-1">
                                    <MapPin size={14} />
                                    <span className="truncate" title={pkg.hotel}>{pkg.hotel}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1">
                                  <Clock size={14} />
                                  <span>{pkg.duracion}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  <span>
                                    {pkg.fecha_salida ? 
                                      new Date(pkg.fecha_salida).toLocaleDateString('es-ES') : 
                                      'Fecha flexible'
                                    }
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span>👥 {pkg.disponibilidad || pkg.cupos || 0} lugares disponibles</span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div>
                                {(() => {
                                    // ✅ NUEVO: Usar calcularPrecioFinal
                                    // (Asegúrate de importar al inicio: import { calcularPrecioFinal } from '../utils/precioCalculator.mjs';)
                                    const precioInfo = calcularPrecioFinal(pkg, 1);
                                    
                                    const {
                                      total,
                                      precioBase,
                                      impuestos,
                                      tieneMonedasMixtas,
                                      tarifaAdicional,
                                      monedaTarifaAdicional
                                    } = precioInfo;

                                    // ✅ La tarifa en BD es el precio ORIGINAL (sin descuento)
                                    // El descuento se APLICA para calcular el precio final
                                    const tarifaBD = parseFloat(pkg.tarifa) || 0;
                                    let precioConDescuento = tarifaBD;
                                    let tieneDescuento = false;

                                    if (pkg.descuento_principal_monto && parseFloat(pkg.descuento_principal_monto) > 0) {
                                      tieneDescuento = true;
                                      const descuentoValor = parseFloat(pkg.descuento_principal_monto);
                                      
                                      if (pkg.descuento_principal_tipo === 'porcentaje') {
                                        // Precio con descuento = tarifa * (1 - descuento/100)
                                        precioConDescuento = Math.round(tarifaBD * (1 - descuentoValor / 100));
                                      } else {
                                        // Si es fijo, restar el descuento
                                        precioConDescuento = tarifaBD - descuentoValor;
                                      }
                                    }

                                    // 📅 CÓDIGO COMENTADO - Validación de fechas de vigencia
                                    // if (pkg.descuento_principal_monto && parseFloat(pkg.descuento_principal_monto) > 0) {
                                    //   const ahora = new Date();
                                    //   const desde = pkg.descuento_principal_desde ? new Date(pkg.descuento_principal_desde) : null;
                                    //   const hasta = pkg.descuento_principal_hasta ? new Date(pkg.descuento_principal_hasta) : null;
                                    //   
                                    //   const descuentoActivo = (!desde || ahora >= desde) && (!hasta || ahora <= hasta);
                                    //   
                                    //   if (descuentoActivo) {
                                    //     tieneDescuento = true;
                                    //     if (pkg.descuento_principal_tipo === 'porcentaje') {
                                    //       descuentoMonto = (precioBase * parseFloat(pkg.descuento_principal_monto)) / 100;
                                    //     } else {
                                    //       descuentoMonto = parseFloat(pkg.descuento_principal_monto);
                                    //     }
                                    //   }
                                    // }
                                    
                                    const esSingle = (pkg.singles || '').toLowerCase().trim() === 'si' || 
                                                    (pkg.singles || '').toLowerCase().trim() === 'sí';
                                    
                                    // ✅ DETECTAR MONEDAS MIXTAS directamente del paquete
                                    const tarifaAdicionalPkg = parseFloat(pkg.tarifa_adicional) || 0;
                                    const monedaAdicionalPkg = pkg.moneda_tarifa_adicional || '';
                                    const monedaPrincipal = pkg.moneda || '$';
                                    
                                    // Es mixta si: hay tarifa adicional > 0 Y la moneda adicional es diferente (ej: pesos + USD)
                                    const esMixtaDirecta = tarifaAdicionalPkg > 0 && 
                                                          monedaAdicionalPkg && 
                                                          monedaAdicionalPkg !== monedaPrincipal;
                                    
                                    // Usar la detección directa O la de calcularPrecioFinal
                                    const mostrarMixta = esMixtaDirecta || tieneMonedasMixtas;
                                    const monedaAdicionalFinal = esMixtaDirecta ? monedaAdicionalPkg : monedaTarifaAdicional;
                                    const tarifaAdicionalFinal = esMixtaDirecta ? tarifaAdicionalPkg : tarifaAdicional;
                                    
                                    return (
                                      <div>
                                        {/* ✅ PRECIO PRINCIPAL - AHORA CON MONEDAS MIXTAS */}
                                        <div className="mb-1">
                                          {mostrarMixta ? (
                                            <div className="flex items-baseline gap-1 flex-wrap">
                                              <span className="text-lg text-gray-600">$</span>
                                              <span className="text-2xl font-bold text-[#1E3A5F]">
                                                {Math.round(tarifaBD).toLocaleString('es-AR')}
                                              </span>
                                              <span className="text-gray-500 text-xs mx-0.5">+</span>
                                              <span className="text-2xl font-bold text-blue-600">
                                                {monedaAdicionalFinal} {Math.round(tarifaAdicionalFinal).toLocaleString('es-AR')}
                                              </span>
                                            </div>
                                          ) : (
                                            <div className="flex items-baseline gap-1 flex-wrap">
                                              <span className="text-lg text-gray-600">
                                                {pkg.moneda || '$'}
                                              </span>
                                              <span className="text-2xl font-bold text-[#1E3A5F]">
                                                {Math.round(precioConDescuento).toLocaleString('es-AR')}
                                              </span>
                                            </div>
                                          )}
                                        </div>

                                        {/* DESGLOSE - SOLO si NO hay monedas mixtas */}
                                        {!tieneMonedasMixtas && (
                                          <div className="text-xs text-gray-500 space-y-0.5">
                                            {/* ✅ Precio ORIGINAL tachado + descuento */}
                                            {tieneDescuento && tarifaBD > precioConDescuento && (
                                              <div className="flex items-center gap-2">
                                                <span className="line-through text-gray-400">
                                                  {pkg.moneda || '$'} {Math.round(tarifaBD).toLocaleString('es-AR')}
                                                </span>
                                                <span className="text-green-600 font-semibold">
                                                  {Math.round(parseFloat(pkg.descuento_principal_monto))}
                                                  {pkg.descuento_principal_tipo === 'porcentaje' ? '% OFF' : ' OFF'}
                                                </span>
                                              </div>
                                            )}
                                            
                                            {impuestos > 0 && (
                                              <div>+ Impuestos: {pkg.moneda || '$'} {Math.round(impuestos).toLocaleString('es-AR')}</div>
                                            )}
                                          </div>
                                        )}

                                        {/* Circuito 1 - SIEMPRE mostrar si hay monedas mixtas 
                                        {tieneMonedasMixtas && (
                                          <div className="text-xs text-gray-500 mt-2">
                                            <div className="border-t border-gray-300 pt-1">
                                              <div className="text-blue-600 font-semibold">
                                                Circuito 1: {monedaTarifaAdicional} {Math.round(tarifaAdicional).toLocaleString('es-AR')}
                                              </div>
                                            </div>
                                          </div>
                                        )}*/}

                                        {/* ACLARACIÓN */}
                                        <span className="text-base text-gray-500 block mt-2">
                                          {esSingle ? 'Tarifa Single' : 'Tarifa por persona en base doble/triple'}
                                        </span>

                                        {/* BADGES DE DESCUENTOS - SIN CAMBIOS */}
                                        <div className="flex flex-wrap gap-2 mt-2">
                                          {/* Descuento 2do pasajero */}
                                          {pkg.descuento_2do_pasajero_monto && parseFloat(pkg.descuento_2do_pasajero_monto) > 0 && (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-700 border-blue-300">
                                              <span>👥</span>
                                              <span>
                                                {pkg.descuento_2do_pasajero_tipo === 'porcentaje'
                                                  ? `${Math.round(parseFloat(pkg.descuento_2do_pasajero_monto))}% OFF 2do pasajero`
                                                  : `$${Math.round(parseFloat(pkg.descuento_2do_pasajero_monto)).toLocaleString()} OFF 2do pasajero`
                                                }
                                              </span>
                                            </span>
                                          )}
                                          
                                          {/* Descuento 3er pasajero */}
                                          {pkg.descuento_3er_pasajero_monto && parseFloat(pkg.descuento_3er_pasajero_monto) > 0 && (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-green-100 text-green-700 border-green-300">
                                              <span>👨‍👩‍👧</span>
                                              <span>
                                                {pkg.descuento_3er_pasajero_tipo === 'porcentaje'
                                                  ? `${Math.round(parseFloat(pkg.descuento_3er_pasajero_monto))}% OFF desde 3er pasajero`
                                                  : `$${Math.round(parseFloat(pkg.descuento_3er_pasajero_monto)).toLocaleString()} OFF desde 3er pasajero`
                                                }
                                              </span>
                                            </span>
                                          )}
                                          
                                          {/* Family Plan */}
                                          {pkg.family_plan_activo && parseFloat(pkg.family_plan_descuento) > 0 && (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border-2"
                                              style={{
                                                backgroundColor: pkg.family_plan_estilo?.color_fondo || '#FF6B6B',
                                                color: pkg.family_plan_estilo?.color_fuente || '#FFFFFF',
                                                fontWeight: pkg.family_plan_estilo?.texto_negrita ? 'bold' : 'normal'
                                              }}
                                            >
                                              <span>👨‍👩‍👧‍👦</span>
                                              <span>
                                                {pkg.family_plan_tipo === 'porcentaje'
                                                  ? `Family Plan: ${Math.round(parseFloat(pkg.family_plan_descuento))}% OFF menores`
                                                  : `Family Plan: $${Math.round(parseFloat(pkg.family_plan_descuento)).toLocaleString()} OFF menores`
                                                }
                                              </span>
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                {/*<DescuentosBadges paquete={pkg} />*/}
                              </div>


                              <div className="flex gap-2">
                                <a 
                                    href={`/paquete/${pkg.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Si no es Ctrl+Click ni Click medio, usar navigate para SPA
                                      if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                        e.preventDefault();
                                        navigate(`/paquete/${pkg.id}`);
                                      }
                                    }}
                                    className="px-4 py-2 border border-[#2AA8B0] text-[#2AA8B0] rounded-lg hover:bg-[#2AA8B0] hover:text-white transition-colors inline-block text-center"
                                  >
                                    Ver detalles
                                  </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Paginación */}
              {totalPaginas > 1 && (
                <div className="mt-8 px-6 py-4 bg-white rounded-lg shadow-md">
                  <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-gray-600">
                      Mostrando {indiceInicio + 1} - {Math.min(indiceFin, filteredPackages.length)} de {filteredPackages.length} paquetes
                    </div>
                    
                    <div className="flex gap-2 items-center flex-wrap justify-center">
                      <button
                        onClick={() => setPaginaActual(prev => Math.max(1, prev - 1))}
                        disabled={paginaActual === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Anterior
                      </button>
                      
                      <div className="flex gap-1">
                        <button
                          onClick={() => setPaginaActual(1)}
                          className={`px-4 py-2 rounded-lg ${
                            paginaActual === 1
                              ? 'bg-[#2AA8B0] text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          1
                        </button>
                        
                        {paginaActual > 3 && <span className="px-2 py-2">...</span>}
                        
                        {[...Array(totalPaginas)].map((_, i) => {
                          const pageNum = i + 1;
                          if (pageNum === 1 || pageNum === totalPaginas) return null;
                          if (pageNum < paginaActual - 1 || pageNum > paginaActual + 1) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPaginaActual(pageNum)}
                              className={`px-4 py-2 rounded-lg ${
                                paginaActual === pageNum
                                  ? 'bg-[#2AA8B0] text-white'
                                  : 'border border-gray-300 hover:bg-gray-50'
                                  }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        {paginaActual < totalPaginas - 2 && <span className="px-2 py-2">...</span>}
                        
                        {totalPaginas > 1 && (
                          <button
                            onClick={() => setPaginaActual(totalPaginas)}
                            className={`px-4 py-2 rounded-lg ${
                              paginaActual === totalPaginas
                                ? 'bg-[#2AA8B0] text-white'
                                : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {totalPaginas}
                          </button>
                        )}
                      </div>
                      
                      <button
                        onClick={() => setPaginaActual(prev => Math.min(totalPaginas, prev + 1))}
                        disabled={paginaActual === totalPaginas}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Secciones adicionales */}
        <div className="mt-12">
          {/* ✅ COMENTADO: EuropeSection 
          <EuropeSection />
          */}
          <NewsletterSection />
        </div>
      </div>
      
      <Footer />
    </>
  );
}

export default Paquetes;